import React, {useState} from 'react';
import ConfirmableButton from '../ConfirmableButton/ConfirmableButton';
import Translate from '@src/components/Translate/Translate';
import {IFileModel} from '@src/@interfaces/models/FIle/IFileModel';
import {MakeRequired} from '@src/@types/RequireOne';
import classnames from '@src/helpers/classnames';
import ImageAttach from './file-types/ImageAttach';
import FileAttach from './file-types/FileAttach';
import app from '@src/helpers/app';
import {FormFieldConfigSize} from '@src/@types/components/Form';

type RequiredAttributesOfFile = 'file_name' | 'file_extension' | 'file_mime_type';

export type FileType = MakeRequired<Partial<IFileModel>, RequiredAttributesOfFile>;
export type ImageType = { src: string, alt: string };

export type SupportedFiles = ImageType | FileType;

export default function DismissibleAttach(props: {
    file?: SupportedFiles | null,
    dismissWithoutConfirmation?: boolean,
    onDownload?: React.MouseEventHandler,
    onDismiss?: (e: React.MouseEvent, setDisable: (state: boolean) => void) => void,
    disabled?: boolean,
    size?: FormFieldConfigSize,
}) {
    const {file, onDismiss, onDownload, dismissWithoutConfirmation, disabled = false} = props;
    const [isDisabled, setDisabled] = useState(disabled);

    if (!file) {
        return null;
    }

    const dismissButton = (() => {
        if (!onDismiss) {
            return null;
        }

        if (dismissWithoutConfirmation) {
            return <Translate<HTMLButtonElement>
                    message="DismissibleAttach.dismiss"
                    className="dismissible-attach__dismiss-btn"
                    tag="button"
                    type="button"
                    onClick={(e) => onDismiss(e, setDisabled)}
            />;
        }

        return <ConfirmableButton
                color="danger"
                size="sm"
                onClick={(e) => onDismiss(e, setDisabled)}
                disabled={isDisabled}
                buttonLabel="DismissibleAttach.dismiss"
                onlyOnePopoverCanBeOpened={true}
                className="dismissible-attach__dismiss-btn"
        />;
    })();

    const downloadButton = (() => {
        if (!onDownload) {
            return null;
        }

        return <Translate<HTMLButtonElement>
                message="DismissibleAttach.download"
                className="dismissible-attach__download-btn"
                tag="button"
                type="button"
                onClick={onDownload}
        />;
    })();

    let isImage = true;
    const representation = (() => {
        if ('src' in file) {
            return <ImageAttach
                    src={file.src}
                    alt={file.alt}
                    onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();

                        app('files-viewer').open(file.src);
                    }}
            />;
        }

        if (file.asset_file_path && (file.is_image || file.file_mime_type?.startsWith('image/'))) {
            return <ImageAttach
                    src={file.asset_file_path}
                    alt={file.public_name ?? file.file_name}
                    onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();

                        app('files-viewer').open(file as IFileModel);
                    }}
            />;
        }

        isImage = false;

        return <FileAttach
                file={file}
                onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();

                    app('files-viewer').open(file as IFileModel);
                }}
        />;
    })();

    return <div className={classnames('dismissible-attach', {
        'dismissible-attach--not-image': !isImage,
        [`dismissible-attach--${props.size}`]: props.size,
    })}>
        <div className="dismissible-attach__controls">
            {downloadButton}
            {dismissButton}
        </div>

        <div className="dismissible-attach__body">
            {representation}
        </div>
    </div>;
}
